import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import mqtt from 'mqtt';
import ReactMarkdown from 'react-markdown';

// ─── Constants ──────────────────────────────────────────────
function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

const AGENT_LABELS = {
    educational: 'Educational Design',
    ux_ui: 'UX / UI',
    craft: 'Screenwriting Craft',
    output_structure: 'Output Structure',
};
const AGENT_COLORS = {
    educational: '#3b82f6',
    ux_ui: '#8b5cf6',
    craft: '#f59e0b',
    output_structure: '#10b981',
};
const IMPORTANCE_COLORS = {
    critical: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: 'Critical' },
    high:     { bg: '#fffbeb', border: '#fcd34d', text: '#d97706', label: 'High' },
    medium:   { bg: '#eff6ff', border: '#93c5fd', text: '#2563eb', label: 'Medium' },
    low:      { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', label: 'Low' },
};
const LAYER_LABELS = {
    prompt: 'Prompt', schema: 'Schema', metadata: 'Metadata',
    display: 'Display', educational: 'Educational',
};

// ─── Tiny UI atoms ──────────────────────────────────────────
function ScoreBadge({ score, size = 'md' }) {
    if (score == null) return null;
    const color = score >= 8 ? '#10b981' : score >= 6 ? '#f59e0b' : score >= 4 ? '#f97316' : '#ef4444';
    const dim = size === 'lg' ? 48 : size === 'sm' ? 24 : 32;
    const fs = size === 'lg' ? 18 : size === 'sm' ? 11 : 13;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: dim, height: dim, borderRadius: '50%',
            background: color, color: '#fff', fontWeight: 700, fontSize: fs, flexShrink: 0,
        }}>{score}</span>
    );
}

function SeverityPill({ level }) {
    const c = IMPORTANCE_COLORS[level] || IMPORTANCE_COLORS.medium;
    return (
        <span style={{
            padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px',
            background: c.text + '18', color: c.text,
        }}>{c.label}</span>
    );
}

function AgentTag({ agent }) {
    const color = AGENT_COLORS[agent] || '#6b7280';
    return (
        <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: color + '22', color,
        }}>{AGENT_LABELS[agent] || agent}</span>
    );
}

function LayerTag({ layer }) {
    return (
        <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: '#6b728018', color: '#6b7280',
        }}>{LAYER_LABELS[layer] || layer}</span>
    );
}

function StatusPill({ status }) {
    const map = {
        idle: { bg: '#6b7280', label: 'Idle' },
        running: { bg: '#3b82f6', label: 'Running...' },
        waiting: { bg: '#f59e0b', label: 'Waiting for you' },
        producing_json: { bg: '#8b5cf6', label: 'Producing JSON...' },
        accepted: { bg: '#10b981', label: 'Accepted' },
        stopped: { bg: '#ef4444', label: 'Stopped' },
    };
    const { bg, label } = map[status] || map.idle;
    return (
        <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 999,
            background: bg, color: '#fff', fontSize: 13, fontWeight: 600,
        }}>{label}</span>
    );
}

// ─── Agent Card (compact by default) ────────────────────────
function AgentCard({ name, data, steer, onSteerChange, onRunAgent, running }) {
    const [expanded, setExpanded] = useState(false);
    const label = AGENT_LABELS[name] || name;
    const accent = AGENT_COLORS[name] || '#6b7280';

    const issues = data?.issues || [];
    const suggestions = data?.suggestions || [];
    const questions = data?.follow_up_questions || [];
    const strengths = data?.strengths || [];

    return (
        <div style={{
            border: `1px solid ${accent}33`,
            borderLeft: `4px solid ${accent}`,
            borderRadius: 8, marginBottom: 12, background: '#fff', overflow: 'hidden',
        }}>
            {/* ── Compact header — always visible ── */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', cursor: 'pointer', background: `${accent}08`,
                }}
            >
                <span style={{ fontWeight: 700, color: accent, fontSize: 13 }}>{label}</span>
                {data && <ScoreBadge score={data.score} size="sm" />}
                {data?.confidence != null && (
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {Math.round(data.confidence * 100)}% conf
                    </span>
                )}
                {data?.relevance && data.relevance !== 'high' && (
                    <SeverityPill level={data.relevance} />
                )}
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11 }}>
                    {issues.length > 0 && `${issues.length} issues`}
                    {suggestions.length > 0 && ` / ${suggestions.length} suggestions`}
                    {questions.length > 0 && ` / ${questions.length} Q`}
                </span>
                <span style={{ color: '#9ca3af', fontSize: 14 }}>{expanded ? 'v' : '>'}</span>
            </div>

            {/* ── Summary line — always visible when data exists ── */}
            {data && !expanded && (
                <div style={{ padding: '6px 14px 10px', fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
                    {data.summary}
                </div>
            )}

            {/* ── Expanded detail ── */}
            {expanded && data && (
                <div style={{ padding: '10px 14px 14px' }}>
                    {/* Summary */}
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#374151', lineHeight: 1.5, fontWeight: 500 }}>
                        {data.summary}
                    </p>

                    {/* Strengths */}
                    {strengths.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Strengths</div>
                            {strengths.map((s, i) => (
                                <div key={i} style={{ fontSize: 12, color: '#374151', padding: '2px 0', paddingLeft: 10, borderLeft: '2px solid #10b98144' }}>{s}</div>
                            ))}
                        </div>
                    )}

                    {/* Issues */}
                    {issues.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Issues</div>
                            {issues.map((iss) => (
                                <div key={iss.id} style={{ marginBottom: 6, padding: '6px 8px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                        <SeverityPill level={iss.severity} />
                                        <span style={{ fontWeight: 600, fontSize: 12, color: '#1f2937' }}>{iss.title}</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.4 }}>{iss.description}</div>
                                    {iss.suggested_fix && (
                                        <div style={{ fontSize: 11, color: '#059669', marginTop: 3 }}>
                                            Fix: {iss.suggested_fix}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Suggestions */}
                    {suggestions.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Suggestions</div>
                            {suggestions.map((sug) => (
                                <div key={sug.id} style={{ marginBottom: 4, padding: '4px 8px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
                                        <SeverityPill level={sug.priority} />
                                        <span style={{ fontWeight: 600, fontSize: 12, color: '#1f2937' }}>{sug.title}</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#4b5563' }}>{sug.description}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Questions */}
                    {questions.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Questions</div>
                            {questions.map((q, i) => (
                                <div key={i} style={{ fontSize: 12, color: '#4b5563', padding: '3px 0', display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                    <span>{q.blocking ? '!!' : '?'}</span>
                                    <span>{q.question}{q.blocking && <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 10, marginLeft: 4 }}>BLOCKING</span>}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Raw content fallback (old format migration) */}
                    {data._raw_content && (
                        <details style={{ marginTop: 8 }}>
                            <summary style={{ fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>Full rationale (markdown)</summary>
                            <div style={{ marginTop: 6, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                                <ReactMarkdown>{data._raw_content}</ReactMarkdown>
                            </div>
                        </details>
                    )}

                    {/* Re-run controls */}
                    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="text" placeholder="Steering note..."
                            value={steer || ''}
                            onChange={(e) => onSteerChange(name, e.target.value)}
                            style={{
                                flex: 1, padding: '5px 8px', borderRadius: 6,
                                border: '1px solid #d1d5db', background: '#f9fafb',
                                color: '#1f2937', fontSize: 12,
                            }}
                        />
                        <button
                            onClick={() => onRunAgent(name)} disabled={running}
                            style={{
                                padding: '5px 12px', borderRadius: 6, border: 'none',
                                background: running ? '#d1d5db' : accent,
                                color: '#fff', fontSize: 12, fontWeight: 600,
                                cursor: running ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                            }}
                        >{running ? 'Running...' : 'Re-run'}</button>
                    </div>
                </div>
            )}

            {/* No data yet */}
            {!data && (
                <div style={{ padding: '8px 14px', color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>
                    Waiting for first round...
                </div>
            )}
        </div>
    );
}

// ─── Question Inbox ─────────────────────────────────────────
function QuestionInbox({ agentOutputs }) {
    const [open, setOpen] = useState(true);
    // Aggregate questions from all agents
    const allQuestions = useMemo(() => {
        const qs = [];
        Object.entries(agentOutputs).forEach(([agentName, data]) => {
            (data.follow_up_questions || []).forEach((q, i) => {
                qs.push({ ...q, agent: agentName, key: `${agentName}_q${i}` });
            });
        });
        // Sort: blocking first, then by agent
        qs.sort((a, b) => (b.blocking ? 1 : 0) - (a.blocking ? 1 : 0));
        return qs;
    }, [agentOutputs]);

    if (allQuestions.length === 0) return null;

    const blockingCount = allQuestions.filter(q => q.blocking).length;

    return (
        <div style={{
            border: '1px solid #7c3aed33', borderLeft: '4px solid #7c3aed',
            borderRadius: 8, marginBottom: 16, background: '#fff',
        }}>
            <div
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', cursor: 'pointer', background: '#7c3aed08',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, color: '#7c3aed', fontSize: 14 }}>
                        Questions from Agents
                    </span>
                    <span style={{
                        padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: blockingCount > 0 ? '#dc262618' : '#7c3aed18',
                        color: blockingCount > 0 ? '#dc2626' : '#7c3aed',
                    }}>
                        {allQuestions.length}{blockingCount > 0 ? ` (${blockingCount} blocking)` : ''}
                    </span>
                </div>
                <span style={{ color: '#9ca3af', fontSize: 14 }}>{open ? 'v' : '>'}</span>
            </div>
            {open && (
                <div style={{ padding: '8px 14px 12px' }}>
                    {allQuestions.map((q) => (
                        <div key={q.key} style={{
                            display: 'flex', gap: 8, padding: '6px 0',
                            borderBottom: '1px solid #f3f4f6', alignItems: 'baseline',
                        }}>
                            <AgentTag agent={q.agent} />
                            <span style={{ fontSize: 13, color: '#1f2937', flex: 1 }}>{q.question}</span>
                            {q.blocking && (
                                <span style={{
                                    padding: '1px 6px', borderRadius: 4, fontSize: 10,
                                    fontWeight: 700, background: '#dc262618', color: '#dc2626',
                                }}>BLOCKING</span>
                            )}
                        </div>
                    ))}
                    {blockingCount > 0 && (
                        <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                            Blocking questions may change agent assessments significantly. Consider addressing them via steering before the next round.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Agreements & Tensions ──────────────────────────────────
function AgreementsTensions({ agreements, tensions }) {
    if ((!agreements || agreements.length === 0) && (!tensions || tensions.length === 0)) return null;

    return (
        <div style={{ marginBottom: 14 }}>
            {/* Agreements */}
            {agreements && agreements.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                        Agents Agree
                    </div>
                    {agreements.map((a, i) => (
                        <div key={i} style={{
                            padding: '6px 10px', borderRadius: 6, marginBottom: 4,
                            background: '#f0fdf4', border: '1px solid #bbf7d0',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                <span style={{ fontWeight: 600, fontSize: 12, color: '#1f2937' }}>{a.theme}</span>
                                {(a.agents || []).map(ag => <AgentTag key={ag} agent={ag} />)}
                            </div>
                            <div style={{ fontSize: 12, color: '#4b5563' }}>{a.detail}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tensions */}
            {tensions && tensions.length > 0 && (
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                        Tensions
                    </div>
                    {tensions.map((t, i) => (
                        <div key={i} style={{
                            padding: '8px 10px', borderRadius: 6, marginBottom: 4,
                            background: '#fffbeb', border: '1px solid #fde68a',
                        }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: '#1f2937', marginBottom: 4 }}>{t.topic}</div>
                            {(t.sides || []).map((side, j) => (
                                <div key={j} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 2 }}>
                                    <div style={{ display: 'flex', gap: 3 }}>{(side.agents || []).map(ag => <AgentTag key={ag} agent={ag} />)}</div>
                                    <span style={{ fontSize: 12, color: '#4b5563' }}>{side.position}</span>
                                </div>
                            ))}
                            {t.coordinator_take && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#4338ca', fontWeight: 500, borderTop: '1px solid #fde68a', paddingTop: 4 }}>
                                    Coordinator: {t.coordinator_take}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Proposed Change Item ───────────────────────────────────
function ChangeItem({ change, checked, onToggle }) {
    const imp = IMPORTANCE_COLORS[change.importance] || IMPORTANCE_COLORS.medium;
    return (
        <div
            onClick={onToggle}
            style={{
                display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6,
                border: `1px solid ${checked ? imp.border : '#e5e7eb'}`,
                background: checked ? imp.bg : '#fafafa',
                marginBottom: 6, cursor: 'pointer',
                opacity: checked ? 1 : 0.55, transition: 'all 0.15s ease',
            }}
        >
            <div style={{ paddingTop: 1, flexShrink: 0 }}>
                <div style={{
                    width: 18, height: 18, borderRadius: 3,
                    border: `2px solid ${checked ? imp.text : '#d1d5db'}`,
                    background: checked ? imp.text : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 12, fontWeight: 700,
                }}>{checked ? '\u2713' : ''}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, fontSize: 12, color: '#1f2937' }}>{change.title}</span>
                    <SeverityPill level={change.importance} />
                    {change.layer && <LayerTag layer={change.layer} />}
                </div>
                <div style={{ display: 'flex', gap: 3, marginBottom: 3, flexWrap: 'wrap' }}>
                    {(change.origin_agents || []).map(ag => <AgentTag key={ag} agent={ag} />)}
                </div>
                <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.4 }}>{change.description}</div>
            </div>
        </div>
    );
}

// ─── Coordinator Panel ──────────────────────────────────────
function CoordinatorPanel({ data, selectedChanges, onToggleChange, onSelectAll, onDeselectAll, onProduceJson, producing }) {
    const [expanded, setExpanded] = useState(true);
    if (!data) return null;

    const changes = data.proposed_changes || [];
    const selectedCount = Object.values(selectedChanges).filter(Boolean).length;

    return (
        <div style={{
            border: '1px solid #6366f144', borderLeft: '4px solid #6366f1',
            borderRadius: 8, background: '#fff', marginBottom: 16,
        }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', cursor: 'pointer', background: '#6366f108',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, color: '#4338ca', fontSize: 14 }}>Coordinator</span>
                    <ScoreBadge score={data.overall} size="lg" />
                    <span style={{ color: '#6b7280', fontSize: 12 }}>Round {data.round}</span>
                </div>
                <span style={{ color: '#9ca3af', fontSize: 14 }}>{expanded ? 'v' : '>'}</span>
            </div>

            {expanded && (
                <div style={{ padding: '12px 14px' }}>
                    {/* Dimension scores */}
                    <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
                        {data.scores && Object.entries(data.scores).map(([dim, score]) => (
                            <div key={dim} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <ScoreBadge score={score} size="sm" />
                                <span style={{ color: '#374151', fontSize: 12 }}>{AGENT_LABELS[dim] || dim}</span>
                            </div>
                        ))}
                    </div>

                    {/* Summary */}
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                        {data.summary}
                    </p>

                    {/* Recommendation */}
                    {data.recommendation && (
                        <div style={{
                            padding: '6px 10px', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#1f2937',
                            background: data.recommendation === 'accept' ? '#10b98122' : '#f59e0b22',
                            border: `1px solid ${data.recommendation === 'accept' ? '#10b98144' : '#f59e0b44'}`,
                        }}>
                            <strong>Recommendation:</strong>{' '}
                            {data.recommendation === 'accept' ? 'Formula is ready — Accept'
                                : data.recommendation === 'iterate_specific'
                                ? `Iterate on ${AGENT_LABELS[data.iterate_target] || data.iterate_target || 'specific agent'}`
                                : 'Keep iterating — run another round'}
                        </div>
                    )}

                    {/* Agreements + Tensions */}
                    <AgreementsTensions agreements={data.agreements} tensions={data.tensions} />

                    {/* ─── Proposed Changes Checklist ─── */}
                    {changes.length > 0 && (
                        <div>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8,
                            }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>
                                    Proposed Changes ({selectedCount}/{changes.length})
                                </span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button onClick={(e) => { e.stopPropagation(); onSelectAll(); }}
                                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 11, cursor: 'pointer' }}>
                                        All
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); onDeselectAll(); }}
                                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 11, cursor: 'pointer' }}>
                                        None
                                    </button>
                                </div>
                            </div>

                            {changes.map((change) => (
                                <ChangeItem
                                    key={change.id}
                                    change={change}
                                    checked={!!selectedChanges[change.id]}
                                    onToggle={() => onToggleChange(change.id)}
                                />
                            ))}

                            <button
                                onClick={onProduceJson}
                                disabled={selectedCount === 0 || producing}
                                style={{
                                    width: '100%', marginTop: 8, padding: '10px 0', borderRadius: 8,
                                    border: 'none', fontSize: 14, fontWeight: 700, color: '#fff',
                                    background: producing ? '#8b5cf6' : selectedCount === 0 ? '#d1d5db' : '#059669',
                                    cursor: producing || selectedCount === 0 ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {producing ? 'Producing JSON...' : `Produce JSON with ${selectedCount} Change${selectedCount !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Activity + Debug Log ────────────────────────────────────
function ActivityLog({ events, debugLogs }) {
    const eventEndRef = useRef(null);
    const debugEndRef = useRef(null);
    const [debugOpen, setDebugOpen] = useState(false);

    useEffect(() => { eventEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [events]);
    useEffect(() => { if (debugOpen) debugEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [debugLogs, debugOpen]);

    if (events.length === 0 && debugLogs.length === 0) return null;
    return (
        <div>
            {events.length > 0 && (
                <div style={{
                    maxHeight: 100, overflowY: 'auto', padding: '6px 10px',
                    background: '#f9fafb', borderRadius: 6, marginBottom: 6,
                    fontSize: 11, fontFamily: 'monospace', color: '#6b7280',
                }}>
                    {events.map((ev, i) => (
                        <div key={i} style={{ marginBottom: 1 }}>
                            <span style={{ color: '#9ca3af' }}>{ev.time}</span>{' '}
                            <span style={{ color: ev.level === 'error' ? '#dc2626' : '#374151' }}>{ev.text}</span>
                        </div>
                    ))}
                    <div ref={eventEndRef} />
                </div>
            )}
            {debugLogs.length > 0 && (
                <div style={{ borderRadius: 6, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    <div onClick={() => setDebugOpen(!debugOpen)} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 10px', background: '#f3f4f6', cursor: 'pointer',
                        fontSize: 10, fontWeight: 600, color: '#6b7280', userSelect: 'none',
                    }}>
                        <span>Debug ({debugLogs.length})</span>
                        <span>{debugOpen ? 'v' : '>'}</span>
                    </div>
                    {debugOpen && (
                        <div style={{
                            maxHeight: 180, overflowY: 'auto', padding: '4px 8px',
                            background: '#1f2937', fontSize: 10,
                            fontFamily: '"SF Mono", "Fira Code", Consolas, monospace', lineHeight: 1.4,
                        }}>
                            {debugLogs.map((log, i) => (
                                <div key={i} style={{ marginBottom: 1, wordBreak: 'break-all' }}>
                                    <span style={{ color: '#6b7280' }}>{log.time}</span>{' '}
                                    <span style={{
                                        color: log.level === 'error' ? '#fca5a5'
                                            : log.level === 'warn' ? '#fcd34d'
                                            : log.level === 'mqtt' ? '#a5b4fc' : '#9ca3af'
                                    }}>
                                        {log.level === 'mqtt' ? '[MQTT] ' : log.level === 'error' ? '[ERR] ' : log.level === 'warn' ? '[WARN] ' : ''}
                                    </span>
                                    <span style={{ color: '#e5e7eb' }}>{log.text}</span>
                                </div>
                            ))}
                            <div ref={debugEndRef} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main Page ──────────────────────────────────────────────
export default function FormulaStudioIndex() {
    const [sessionId, setSessionId] = useState(null);
    const [sessionStatus, setSessionStatus] = useState('idle');
    const [roundNum, setRoundNum] = useState(0);
    const [agentOutputs, setAgentOutputs] = useState({});
    const [coordinatorOutput, setCoordinatorOutput] = useState(null);
    const [formulaJson, setFormulaJson] = useState(null);
    const [jsonPaneOpen, setJsonPaneOpen] = useState(true);
    const [selectedChanges, setSelectedChanges] = useState({});
    const [producingJson, setProducingJson] = useState(false);
    const [formulaBrief, setFormulaBrief] = useState('');
    const [globalSteer, setGlobalSteer] = useState('');
    const [agentSteers, setAgentSteers] = useState({});
    const clientRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [debugLogs, setDebugLogs] = useState([]);

    const addEvent = useCallback((text, level = 'event') => {
        setEvents(prev => [...prev, { time: new Date().toLocaleTimeString(), text, level }]);
    }, []);
    const addDebug = useCallback((text, level = 'debug') => {
        setDebugLogs(prev => [...prev.slice(-200), { time: new Date().toLocaleTimeString(), text, level }]);
    }, []);

    useEffect(() => { return () => { if (clientRef.current) clientRef.current.end(true); }; }, []);

    // ── MQTT handler ──
    const handleMqttMessage = useCallback((topic, message) => {
        const raw = message.toString();
        let payload;
        try { payload = JSON.parse(raw); }
        catch (err) {
            addDebug(`JSON parse error on ${topic}: ${err.message}`, 'error');
            return;
        }

        const topicShort = topic.split('/').slice(-2).join('/');
        addDebug(`${topicShort} (${raw.length}b) keys=[${Object.keys(payload).join(', ')}]`, 'mqtt');

        if (topic.includes('/status')) {
            const ev = payload.event;
            addEvent(`Status: ${ev}` + (payload.agent ? ` [${payload.agent}]` : ''));
            if (ev === 'session_started') setSessionStatus('running');
            else if (ev === 'round_started') { setRoundNum(payload.round); setSessionStatus('running'); }
            else if (ev === 'waiting_for_human') { setSessionStatus('waiting'); setProducingJson(false); }
            else if (ev === 'producing_json') { setSessionStatus('producing_json'); setProducingJson(true); }
            else if (ev === 'json_produced') { setProducingJson(false); addEvent(`Formula JSON produced (round ${payload.round})`); }
            else if (ev === 'json_production_failed') { setProducingJson(false); addEvent(`JSON production failed: ${payload.error || 'unknown'}`, 'error'); }
            else if (ev === 'session_accepted') setSessionStatus('accepted');
            else if (ev === 'session_stopped') setSessionStatus('stopped');
            else if (ev === 'coordinator_done') setRoundNum(payload.round);
        } else if (topic.includes('/agent/')) {
            const match = topic.match(/\/agent\/([^/]+)\/output/);
            if (match) {
                const agentName = match[1];
                setAgentOutputs(prev => ({ ...prev, [agentName]: payload }));
                addEvent(`${AGENT_LABELS[agentName] || agentName}: score ${payload.score}, ${(payload.issues||[]).length} issues`);
                addDebug(`Agent ${agentName}: score=${payload.score}, conf=${payload.confidence}, issues=${(payload.issues||[]).length}, sug=${(payload.suggestions||[]).length}, q=${(payload.follow_up_questions||[]).length}`, 'debug');
            }
        } else if (topic.includes('/coordinator/synthesis')) {
            setCoordinatorOutput(payload);
            const pc = payload.proposed_changes || [];
            addEvent(`Coordinator: overall ${payload.overall}/10, ${pc.length} changes, ${(payload.agreements||[]).length} agreements, ${(payload.tensions||[]).length} tensions`);
            addDebug(`proposed_changes: ${pc.length} items`, 'mqtt');
            pc.forEach((c, i) => {
                addDebug(`  [${i}] id=${c.id} "${c.title}" (${c.importance}) layer=${c.layer} from=[${(c.origin_agents||[]).join(',')}]`, 'debug');
            });
            if (pc.length === 0) addDebug('WARNING: No proposed_changes!', 'warn');
            const newSelected = {};
            pc.forEach(c => { newSelected[c.id] = true; });
            setSelectedChanges(newSelected);
        } else if (topic.includes('/formula_json')) {
            if (payload.formula_json) {
                setFormulaJson(payload.formula_json);
                addEvent(`Formula JSON updated (round ${payload.round})`);
                addDebug(`formula_json: ${Object.keys(payload.formula_json).length} keys`, 'mqtt');
            } else {
                addDebug('formula_json payload was null', 'warn');
            }
        }
    }, [addEvent, addDebug]);

    // ── Start ──
    const startSession = async () => {
        if (!formulaBrief.trim()) return;
        const sid = uuid();
        setSessionId(sid); setSessionStatus('running'); setRoundNum(0);
        setAgentOutputs({}); setCoordinatorOutput(null); setFormulaJson(null);
        setSelectedChanges({}); setEvents([]); setDebugLogs([]);
        addEvent('Starting session...'); addDebug(`Session ID: ${sid}`, 'debug');

        const client = mqtt.connect('ws://localhost:9001');
        clientRef.current = client;
        client.on('connect', () => { client.subscribe(`formula/${sid}/#`); addEvent('Connected to MQTT'); addDebug(`Subscribed formula/${sid}/#`, 'mqtt'); });
        client.on('error', err => { addEvent(`MQTT error: ${err.message}`, 'error'); });
        client.on('close', () => addDebug('MQTT closed', 'warn'));
        client.on('reconnect', () => addDebug('MQTT reconnecting...', 'warn'));
        client.on('message', handleMqttMessage);

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        try {
            const res = await fetch('/api/formula-studio/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
                body: JSON.stringify({ session_id: sid, formula_brief: formulaBrief }),
            });
            const data = await res.json();
            if (data.success) addEvent('Python process spawned');
            else { addEvent('Failed to start session'); setSessionStatus('idle'); }
        } catch (err) { addEvent(`HTTP error: ${err.message}`); setSessionStatus('idle'); }
    };

    // ── Controls ──
    const publishControl = (payload) => {
        if (!clientRef.current || !sessionId) return;
        const msg = JSON.stringify(payload);
        clientRef.current.publish(`formula/${sessionId}/control`, msg, { qos: 1 });
        addEvent(`Sent: ${payload.action}`);
        addDebug(`Control: ${msg.slice(0, 200)}`, 'mqtt');
    };

    const handleNextRound = () => publishControl({ action: 'next_round' });
    const handleNextRoundSteer = () => { if (!globalSteer.trim()) return; publishControl({ action: 'next_round_steer', steer: globalSteer }); setGlobalSteer(''); };
    const handleRunAgent = (name) => { publishControl({ action: 'run_agent', target: name, steer: agentSteers[name] || null }); setAgentSteers(p => ({ ...p, [name]: '' })); };
    const handleAccept = () => publishControl({ action: 'accept' });
    const handleStop = () => publishControl({ action: 'stop' });

    const handleToggleChange = (id) => setSelectedChanges(p => ({ ...p, [id]: !p[id] }));
    const handleSelectAll = () => { const pc = coordinatorOutput?.proposed_changes || []; const s = {}; pc.forEach(c => { s[c.id] = true; }); setSelectedChanges(s); };
    const handleDeselectAll = () => setSelectedChanges({});
    const handleProduceJson = () => {
        const ids = Object.entries(selectedChanges).filter(([,v]) => v).map(([k]) => k);
        if (ids.length === 0) return;
        setProducingJson(true);
        publishControl({ action: 'produce_json', selected_changes: ids });
    };

    const isActive = sessionStatus === 'running' || sessionStatus === 'waiting' || sessionStatus === 'producing_json';
    const isWaiting = sessionStatus === 'waiting';

    return (
        <div style={{
            display: 'flex', height: '100vh', background: '#f5f5f5', color: '#1f2937',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        }}>
            {/* ═══ LEFT PANEL ═══ */}
            <div style={{
                width: '34%', minWidth: 340, borderRight: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                <div style={{
                    padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#4338ca' }}>Formula Design Studio</h1>
                    <StatusPill status={sessionStatus} />
                </div>

                <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Formula to Review</label>
                    <textarea
                        value={formulaBrief} onChange={e => setFormulaBrief(e.target.value)} disabled={isActive}
                        placeholder="Paste the formula you're building..."
                        style={{
                            width: '100%', height: 240, padding: 10, borderRadius: 8,
                            border: '1px solid #d1d5db', background: isActive ? '#f3f4f6' : '#fff',
                            color: '#1f2937', fontSize: 12, fontFamily: 'monospace',
                            resize: 'vertical', lineHeight: 1.5,
                        }}
                    />

                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        {!isActive && sessionStatus !== 'accepted' && (
                            <button onClick={startSession} disabled={!formulaBrief.trim()} style={{
                                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                                background: formulaBrief.trim() ? '#4f46e5' : '#d1d5db',
                                color: '#fff', fontSize: 13, fontWeight: 700,
                                cursor: formulaBrief.trim() ? 'pointer' : 'not-allowed',
                            }}>Start Session</button>
                        )}
                        {isActive && (
                            <button onClick={handleStop} style={{
                                padding: '9px 18px', borderRadius: 8, border: '1px solid #ef4444',
                                background: 'transparent', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}>Stop</button>
                        )}
                    </div>

                    {isActive && (
                        <div style={{ marginTop: 16 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                                Round: <strong style={{ color: '#1f2937' }}>{roundNum}</strong>
                            </div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Steering Note</label>
                            <textarea
                                value={globalSteer} onChange={e => setGlobalSteer(e.target.value)}
                                placeholder="Inject a note into the next round..."
                                style={{
                                    width: '100%', height: 60, padding: 8, borderRadius: 6,
                                    border: '1px solid #d1d5db', background: '#f9fafb',
                                    color: '#1f2937', fontSize: 12, fontFamily: 'inherit', resize: 'vertical',
                                }}
                            />
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                <button onClick={handleNextRound} disabled={!isWaiting} style={{
                                    flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
                                    background: isWaiting ? '#4f46e5' : '#d1d5db', color: '#fff',
                                    fontSize: 12, fontWeight: 600, cursor: isWaiting ? 'pointer' : 'not-allowed',
                                }}>Next Round</button>
                                <button onClick={handleNextRoundSteer} disabled={!isWaiting || !globalSteer.trim()} style={{
                                    flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
                                    background: isWaiting && globalSteer.trim() ? '#8b5cf6' : '#d1d5db', color: '#fff',
                                    fontSize: 12, fontWeight: 600, cursor: isWaiting && globalSteer.trim() ? 'pointer' : 'not-allowed',
                                }}>+ Steer</button>
                            </div>
                            <button onClick={handleAccept} disabled={!isWaiting} style={{
                                width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 6,
                                border: 'none', background: isWaiting ? '#10b981' : '#d1d5db',
                                color: '#fff', fontSize: 12, fontWeight: 700,
                                cursor: isWaiting ? 'pointer' : 'not-allowed',
                            }}>Accept Formula</button>
                        </div>
                    )}
                </div>

                <div style={{ padding: '0 18px 12px' }}>
                    <ActivityLog events={events} debugLogs={debugLogs} />
                </div>
            </div>

            {/* ═══ RIGHT PANEL ═══ */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
                {sessionStatus === 'idle' ? (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        height: '100%', color: '#9ca3af', fontSize: 14,
                    }}>
                        Paste the formula you're building and click Start Session.
                    </div>
                ) : (
                    <>
                        {/* Agent cards — compact */}
                        {Object.keys(AGENT_LABELS).map(name => (
                            <AgentCard
                                key={name} name={name} data={agentOutputs[name]}
                                steer={agentSteers[name]}
                                onSteerChange={(n, v) => setAgentSteers(p => ({ ...p, [n]: v }))}
                                onRunAgent={handleRunAgent}
                                running={sessionStatus === 'running'}
                            />
                        ))}

                        {/* Question inbox — aggregated from all agents */}
                        <QuestionInbox agentOutputs={agentOutputs} />

                        {/* Coordinator with agreements/tensions + change board */}
                        <CoordinatorPanel
                            data={coordinatorOutput}
                            selectedChanges={selectedChanges}
                            onToggleChange={handleToggleChange}
                            onSelectAll={handleSelectAll}
                            onDeselectAll={handleDeselectAll}
                            onProduceJson={handleProduceJson}
                            producing={producingJson}
                        />

                        {/* Output JSON Pane */}
                        <div style={{
                            border: '1px solid #d1d5db', borderLeft: '4px solid #059669',
                            borderRadius: 8, background: '#fff', marginBottom: 16,
                        }}>
                            <div onClick={() => setJsonPaneOpen(!jsonPaneOpen)} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '10px 14px', cursor: 'pointer', background: '#05966908',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: 700, color: '#059669', fontSize: 14 }}>Output Formula JSON</span>
                                    {formulaJson ? (
                                        <span style={{ padding: '1px 7px', borderRadius: 4, background: '#10b98122', color: '#059669', fontSize: 11, fontWeight: 600 }}>
                                            Round {roundNum}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#9ca3af', fontSize: 11, fontStyle: 'italic' }}>select changes above and click Produce JSON</span>
                                    )}
                                </div>
                                <span style={{ color: '#9ca3af', fontSize: 14 }}>{jsonPaneOpen ? 'v' : '>'}</span>
                            </div>
                            {jsonPaneOpen && (
                                <div style={{ padding: '10px 14px' }}>
                                    {formulaJson ? (
                                        <>
                                            <pre style={{
                                                background: '#f9fafb', border: '1px solid #e5e7eb',
                                                borderRadius: 6, padding: 12, fontSize: 11, lineHeight: 1.4,
                                                color: '#374151', fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                                                overflow: 'auto', maxHeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                            }}>{JSON.stringify(formulaJson, null, 2)}</pre>
                                            <button
                                                onClick={() => { navigator.clipboard.writeText(JSON.stringify(formulaJson, null, 2)); addEvent('JSON copied'); }}
                                                style={{
                                                    marginTop: 8, padding: '5px 12px', borderRadius: 6,
                                                    border: '1px solid #d1d5db', background: '#fff',
                                                    color: '#374151', fontSize: 12, cursor: 'pointer',
                                                }}
                                            >Copy JSON</button>
                                        </>
                                    ) : (
                                        <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 13 }}>
                                            Review proposed changes above, select what to include, then click "Produce JSON".
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
